import React, { Component } from 'react';
import PropTypes from 'prop-types';

/**
 * Vollflaechiger Ladescreen des Webkonfigurators.
 *
 * Zeigt keinen technischen Fortschrittstext, sondern eine sich aufbauende
 * Website-Szene, wechselnde Begleittexte und einen weich nachlaufenden
 * Fortschrittsbalken. Der echte Fortschritt kommt ueber `progress` /
 * `progressSteps` aus dem SSE-Stream von Olympus.
 */

const STAGES = [
  { key: 'open', label: 'Konfigurator öffnen' },
  { key: 'schema', label: 'Bauplan lesen' },
  { key: 'elements', label: 'Bausteine holen' },
  { key: 'pages', label: 'Seiten aufbauen' },
  { key: 'answers', label: 'Deine Inhalte' },
];

// Grobe Zuordnung der Olympus-Meldungen auf eine Stufe. Unbekannte Meldungen
// fallen auf die Prozent-Schwellen zurueck.
const STAGE_BY_MESSAGE = [
  { match: /schema/i, stage: 1 },
  { match: /element/i, stage: 2 },
  { match: /page/i, stage: 3 },
  { match: /finish/i, stage: 4 },
];

const HEADLINES = [
  'Wir bauen deinen Konfigurator auf',
  'Deine Website wird zusammengesetzt',
  'Fast fertig eingerichtet',
];

const MESSAGES = [
  'Wir lesen den Bauplan deiner Website.',
  'Jede Farbe, jede Schrift, jedes Modul wird einzeln abgeholt.',
  'Deine Inhalte werden ausgepackt – Texte, Bilder, Einstellungen.',
  'Alles wandert gerade an seinen Platz.',
  'Große Projekte haben viele Seiten. Die zählen wir gerade durch.',
  'Noch einen Moment – der Feinschliff läuft.',
];

const TICK_MS = 80;
const MESSAGE_MS = 3800;

// Die kompakte Variante wird in der Seitenvorschau und beim Speichern benutzt,
// damit ueberall dieselbe Bildsprache laeuft.
const PRESETS = {
  preview: {
    headline: 'Deine Vorschau wird gerendert',
    messages: [
      'Wir bauen die Seite mit deinen Einstellungen neu auf.',
      'Bilder, Farben und Texte wandern an ihren Platz.',
      'Gleich siehst du, wie es live aussieht.',
    ],
  },
  saving: {
    headline: 'Wir speichern deine Inhalte',
    messages: [
      'Deine Antworten werden gesichert.',
      'Wir erzeugen die Seite und die Vorschau gleich mit.',
      'Das dauert einen Moment – bitte das Fenster offen lassen.',
    ],
  },
};

class WebconfigLoader extends Component {
  constructor(props) {
    super(props);
    this.state = {
      shown: 0,
      messageIndex: 0,
      elapsed: 0,
    };
    this.tick = this.tick.bind(this);
    this.tickTimer = null;
    this.messageTimer = null;
    this.startedAt = Date.now();
  }

  componentDidMount() {
    this.tickTimer = setInterval(this.tick, TICK_MS);
    this.messageTimer = setInterval(() => {
      this.setState((s) => ({ messageIndex: s.messageIndex + 1 }));
    }, MESSAGE_MS);
  }

  componentWillUnmount() {
    clearInterval(this.tickTimer);
    clearInterval(this.messageTimer);
  }

  /**
   * Der Balken folgt dem echten Wert weich nach und kriecht bei Stillstand
   * langsam weiter, damit er nie eingefroren wirkt. Zurueck geht er nie.
   */
  tick() {
    const target = this.getTargetPercent();
    this.setState((s) => {
      const gap = target - s.shown;
      let next = s.shown;
      if (gap > 0.2) {
        next = s.shown + Math.max(gap * 0.12, 0.15);
      } else {
        // Leerlauf: hoechstens bis kurz vor die naechste echte Marke kriechen
        next = Math.min(s.shown + 0.05, Math.min(target + 6, 97));
      }
      return {
        shown: Math.min(Math.max(next, s.shown), 99),
        elapsed: Date.now() - this.startedAt,
      };
    });
  }

  getTargetPercent() {
    const m = this.props.message;
    if (!m || !m.progressSteps) {
      return 4;
    }
    const pct = (m.progress / m.progressSteps) * 100;
    if (isNaN(pct)) {
      return 4;
    }
    return Math.max(0, Math.min(pct, 100));
  }

  getStageIndex() {
    const m = this.props.message;
    if (m && m.message) {
      for (const entry of STAGE_BY_MESSAGE) {
        if (entry.match.test(m.message)) {
          return entry.stage;
        }
      }
    }
    const pct = this.state.shown;
    if (pct < 12) return 0;
    if (pct < 28) return 1;
    if (pct < 65) return 2;
    if (pct < 92) return 3;
    return 4;
  }

  renderScene() {
    // Reine CSS-Animation, damit auf langsamen Rechnern nichts nachgerendert
    // werden muss: die Bloecke bauen sich in einer Schleife immer wieder auf.
    const lines = [0, 1, 2];
    return (
      <div className={'wcl-scene'}>
        <div className={'wcl-glow'} />
        <svg viewBox="0 0 360 250" className={'wcl-svg'} role="presentation">
          <g className={'wcl-window'}>
            <rect
              x="30"
              y="26"
              width="300"
              height="198"
              rx="14"
              className={'wcl-frame'}
            />
            <rect
              x="30"
              y="26"
              width="300"
              height="26"
              rx="14"
              className={'wcl-bar'}
            />
            <rect x="30" y="40" width="300" height="12" className={'wcl-bar'} />
            <circle cx="48" cy="39" r="4" className={'wcl-dot wcl-dot-1'} />
            <circle cx="62" cy="39" r="4" className={'wcl-dot wcl-dot-2'} />
            <circle cx="76" cy="39" r="4" className={'wcl-dot wcl-dot-3'} />

            <rect
              x="46"
              y="66"
              width="268"
              height="58"
              rx="8"
              className={'wcl-piece wcl-hero'}
            />
            {lines.map((i) => (
              <rect
                key={i}
                x="46"
                y={136 + i * 14}
                width={i === 2 ? 150 : 268}
                height="8"
                rx="4"
                className={'wcl-piece wcl-line wcl-line-' + (i + 1)}
              />
            ))}
            <rect
              x="46"
              y="180"
              width="82"
              height="30"
              rx="6"
              className={'wcl-piece wcl-card wcl-card-1'}
            />
            <rect
              x="139"
              y="180"
              width="82"
              height="30"
              rx="6"
              className={'wcl-piece wcl-card wcl-card-2'}
            />
            <rect
              x="232"
              y="180"
              width="82"
              height="30"
              rx="6"
              className={'wcl-piece wcl-card wcl-card-3'}
            />
            <rect
              x="46"
              y="66"
              width="60"
              height="158"
              className={'wcl-shine'}
            />
          </g>

          <g className={'wcl-floaters'}>
            <rect
              x="6"
              y="150"
              width="22"
              height="22"
              rx="6"
              className={'wcl-chip wcl-chip-1'}
            />
            <rect
              x="332"
              y="88"
              width="18"
              height="18"
              rx="5"
              className={'wcl-chip wcl-chip-2'}
            />
            <circle cx="20" cy="70" r="7" className={'wcl-chip wcl-chip-3'} />
            <circle cx="344" cy="176" r="6" className={'wcl-chip wcl-chip-4'} />
          </g>
        </svg>
      </div>
    );
  }

  getPreset() {
    return PRESETS[this.props.preset] || null;
  }

  getHeadline() {
    const preset = this.getPreset();
    if (preset) {
      return preset.headline;
    }
    const stageIndex = this.getStageIndex();
    return HEADLINES[Math.min(Math.floor(stageIndex / 2), HEADLINES.length - 1)];
  }

  getMessages() {
    const preset = this.getPreset();
    return preset ? preset.messages : MESSAGES;
  }

  render() {
    const compact = this.props.variant === 'compact';
    const stageIndex = this.getStageIndex();
    const percent = Math.round(this.state.shown);
    const messages = this.getMessages();
    const message = messages[this.state.messageIndex % messages.length];
    const longWait = this.state.elapsed > 20000;
    // Ohne echten Fortschritt (Vorschau, Speichern) waere ein Balken gelogen –
    // dort laeuft nur die Szene.
    // Beim Speichern gibt es keinen echten Fortschrittswert - dann laeuft ein
    // unbestimmter Balken statt einer erfundenen Prozentzahl.
    const indeterminate = this.props.indeterminate === true;
    const showProgress = !compact && this.props.showProgress !== false;
    const showStages = !compact && !indeterminate;

    return (
      <div className={'wcl-root' + (compact ? ' is-compact' : '')}>
        <div className={'wcl-aurora wcl-aurora-1'} />
        <div className={'wcl-aurora wcl-aurora-2'} />
        {!compact && <div className={'wcl-aurora wcl-aurora-3'} />}

        <div className={'wcl-inner'}>
          {this.renderScene()}

          <h1 className={'wcl-headline'}>{this.getHeadline()}</h1>

          <div className={'wcl-message-slot'}>
            <div className={'wcl-message'} key={this.state.messageIndex}>
              {message}
            </div>
          </div>

          {showProgress && indeterminate && (
            <div className={'wcl-progress'}>
              <div className={'wcl-progress-track'}>
                <div className={'wcl-progress-indeterminate'} />
              </div>
            </div>
          )}

          {showProgress && !indeterminate && (
            <div className={'wcl-progress'}>
              <div className={'wcl-progress-track'}>
                <div
                  className={'wcl-progress-fill'}
                  style={{ width: percent + '%' }}
                >
                  <span className={'wcl-progress-sheen'} />
                </div>
              </div>
              <div className={'wcl-percent'}>{percent}%</div>
            </div>
          )}

          {showStages && (
            <ul className={'wcl-stages'}>
              {STAGES.map((stage, i) => {
                let cls = 'wcl-stage';
                if (i < stageIndex) cls += ' is-done';
                if (i === stageIndex) cls += ' is-active';
                return (
                  <li key={stage.key} className={cls}>
                    <span className={'wcl-stage-dot'} />
                    <span className={'wcl-stage-label'}>{stage.label}</span>
                  </li>
                );
              })}
            </ul>
          )}

          {!compact && (
            <div className={'wcl-hint' + (longWait ? ' is-visible' : '')}>
              {indeterminate
                ? 'Bitte das Fenster offen lassen – wir sind gleich fertig.'
                : 'Dein Projekt bringt viele Inhalte mit – deshalb dauert der Aufbau heute etwas länger.'}
            </div>
          )}
        </div>
      </div>
    );
  }
}

WebconfigLoader.propTypes = {
  message: PropTypes.object,
  variant: PropTypes.string,
  preset: PropTypes.string,
  showProgress: PropTypes.bool,
  indeterminate: PropTypes.bool,
};

export default WebconfigLoader;
